import React, { useState, useEffect } from "react";
import { REQUEST_STATUS_LABELS } from "../utils/diagnosticHelpers";

/**
 * Diagnostic Request View Component
 * Allows employees to select diagnosers and submit a request
 */
const DiagnosticRequestView = ({
  request,
  coworkers,
  isLoadingCoworkers,
  currentUserId,
  onCreateRequest,
  onUpdateRequest,
  isCreating,
  isUpdating
}) => {
  const [selectedDiagnosers, setSelectedDiagnosers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  // Initialize from existing request
  useEffect(() => {
    if (request?.requestedDiagnosers) {
      const ids = request.requestedDiagnosers
        .filter(d => d.status !== 'removed')
        .map(d => d.user?._id || d.user);
      setSelectedDiagnosers(ids);
    }
  }, [request]);

  const toggleDiagnoser = (userId) => {
    setSelectedDiagnosers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSubmit = () => {
    const data = {
      diagnoserIds: selectedDiagnosers,
      includeSelfDiagnosis: true,
    };

    if (request && isEditing) {
      onUpdateRequest(data);
    } else {
      onCreateRequest(data);
    }
    setIsEditing(false);
  };

  // Filter out current user from coworker list
  const availableCoworkers = coworkers?.filter(c => c._id !== currentUserId) || [];

  // Request exists and not editing - show status view
  if (request && !isEditing) {
    const status = REQUEST_STATUS_LABELS[request.requestStatus] || REQUEST_STATUS_LABELS.pending_approval;

    return (
      <div className="diagnostic-card request-card">
        <div className="card-head">
          <h2 className="section-heading">Your Diagnostic Request</h2>
          <span
            className="request-status-badge"
            style={{ background: status.bg, color: status.color }}
          >
            {status.text}
          </span>
        </div>

        <div className="request-details">
          <div className="request-detail-row">
            <span className="request-detail-label">Selected Diagnosers:</span>
            <span className="request-detail-value">
              {request.requestedDiagnosers?.length > 0
                ? request.requestedDiagnosers
                    .filter(d => d.status !== 'removed')
                    .map(d => `${d.user?.firstName} ${d.user?.lastName}`)
                    .join(", ")
                : "None selected"
              }
            </span>
          </div>
          <div className="request-detail-row">
            <span className="request-detail-label">Include Self-Diagnosis:</span>
            <span className="request-detail-value">{request.includeSelfDiagnosis ? "Yes" : "No"}</span>
          </div>
          <div className="request-detail-row">
            <span className="request-detail-label">Submitted:</span>
            <span className="request-detail-value">
              {request.requestedAt
                ? new Date(request.requestedAt).toLocaleDateString()
                : "—"
              }
            </span>
          </div>
          {request.requestStatus === "rejected" && request.rejectionReason && (
            <div className="request-detail-row rejection">
              <span className="request-detail-label">Rejection Reason:</span>
              <span className="request-detail-value">{request.rejectionReason}</span>
            </div>
          )}
        </div>

        {request.requestStatus === "pending_approval" && (
          <div className="request-actions">
            <button
              className="edit-request-btn"
              onClick={() => setIsEditing(true)}
            >
              Edit Request
            </button>
          </div>
        )}

        {request.requestStatus === "rejected" && (
          <div className="request-actions">
            <button
              className="submit-btn"
              onClick={() => setIsEditing(true)}
            >
              Resubmit Request
            </button>
          </div>
        )}
      </div>
    );
  }

  // No request or editing - show form
  return (
    <div className="diagnostic-card request-card">
      <div className="card-head">
        <h2 className="section-heading">
          {request && isEditing ? "Edit Your Request" : "Request Diagnostic Feedback"}
        </h2>
      </div>

      <p className="request-intro">
        Select colleagues who you'd like to provide feedback on your performance.
        Your selection will be reviewed by your administrator before diagnostic forms are assigned.
      </p>

      {/* Coworker selection */}
      <div className="diagnoser-selection">
        <h3 className="selection-title">Select Diagnosers ({selectedDiagnosers.length} selected)</h3>

        {isLoadingCoworkers ? (
          <div className="loading-state">Loading colleagues...</div>
        ) : availableCoworkers.length === 0 ? (
          <div className="empty-state">
            <p>No other employees found in your company.</p>
          </div>
        ) : (
          <div className="coworkers-list">
            {availableCoworkers.map((coworker) => (
              <label
                key={coworker._id}
                className={`coworker-item ${selectedDiagnosers.includes(coworker._id) ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedDiagnosers.includes(coworker._id)}
                  onChange={() => toggleDiagnoser(coworker._id)}
                />
                <div className="coworker-info">
                  <span className="coworker-name">
                    {coworker.firstName} {coworker.lastName}
                  </span>
                  <span className="coworker-email">{coworker.email}</span>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="nav-row">
        {isEditing && (
          <button
            type="button"
            className="ghost-btn"
            onClick={() => setIsEditing(false)}
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          className="submit-btn"
          onClick={handleSubmit}
          disabled={isCreating || isUpdating}
        >
          {isCreating || isUpdating
            ? "Submitting..."
            : request && isEditing
              ? "Update Request →"
              : "Submit Request →"
          }
        </button>
      </div>
    </div>
  );
};

export default DiagnosticRequestView;
